import {
  ProductFormDraftSnapshot,
  ProductFormDraftSummary,
  AttributeGroupDraft,
  ProductModifierGroupDraft,
  ProductVariantEditor,
} from '@features/catalog/models/product-form-draft.models';
import { dedupeCategoryIds } from '@features/catalog/utils/product-category-assignment.utils';
import { ProductBundleFormRawValue } from '@features/catalog/utils/product-bundle-input.builder';
import { PRODUCT_FORM_DRAFT_VERSION } from '@features/catalog/presentation/utils/product-form-draft.storage';

export function normalizeDraftCategoryIds(
  formValue: ProductFormDraftSnapshot['formValue'] & { primaryCategoryId?: string | null }
): string[] {
  const rawIds = Array.isArray(formValue.categoryIds) ? formValue.categoryIds : [];
  const normalized = dedupeCategoryIds(rawIds);
  if (normalized.length) {
    return normalized;
  }
  const legacyPrimary = formValue.primaryCategoryId?.trim();
  return legacyPrimary ? [legacyPrimary] : [];
}

export function normalizeDraftFormValue(
  formValue: ProductFormDraftSnapshot['formValue'] & {
    sendToKitchen?: boolean;
    primaryCategoryId?: string | null;
  }
): ProductFormDraftSnapshot['formValue'] {
  const legacy = formValue as ProductFormDraftSnapshot['formValue'] & { publicVisible?: boolean };
  const posVisible = legacy.posVisible ?? true;
  const publicVisible = legacy.publicVisible ?? posVisible;

  return {
    name: formValue.name ?? '',
    price: Number(formValue.price ?? 0),
    productStructure: formValue.productStructure === 'variable' ? 'variable' : 'simple',
    description: formValue.description ?? '',
    imageUrl: formValue.imageUrl ?? '',
    publicVisible,
    posVisible,
    active: formValue.active ?? true,
    categoryIds: normalizeDraftCategoryIds(formValue),
    sku: formValue.sku ?? '',
    stockQuantity: formValue.stockQuantity ?? null,
    controlInventory: formValue.controlInventory ?? false,
  };
}

export interface BuildProductFormDraftSnapshotInput {
  draftKey: string;
  isEdit: boolean;
  productId: string | null;
  draftName: string;
  formValue: ProductBundleFormRawValue;
  attributeGroups: readonly AttributeGroupDraft[];
  productVariants: readonly ProductVariantEditor[];
  modifierGroups: readonly ProductModifierGroupDraft[];
}

export function buildProductFormDraftSnapshot(
  input: BuildProductFormDraftSnapshotInput
): ProductFormDraftSnapshot {
  const summary: ProductFormDraftSummary = {
    key: input.draftKey,
    mode: input.isEdit ? 'edit' : 'new',
    productId: input.productId,
    name: input.draftName,
    variantCount: input.productVariants.length,
    updatedAt: new Date().toISOString(),
  };

  return {
    version: PRODUCT_FORM_DRAFT_VERSION,
    summary,
    formValue: input.formValue,
    attributeGroups: [...input.attributeGroups],
    productVariants: [...input.productVariants],
    modifierGroups: [...input.modifierGroups],
  };
}
